import { useEffect, useState } from "react";
import { ChevronDown, Loader2, QrCode, ShieldCheck } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { NoteMarkdown } from "@baluu/ui/components/checkout/note-markdown";
import { BookingQRCode__DataUrl } from "@obby/lib";
import { messageOf } from "../../api";
import { describeCancellationTiers, describeReschedulePolicy } from "./format";
import { usePolicy } from "./useClassroom";

// The receipt's two fold-outs: the booking's policy (v1's
// BookingPolicyDialog, which opened a modal; a modal would portal out of
// this element's shadow root, so it opens in place) and the check-in QR
// code (v1's BookingQRCode).

export function PolicyPanel({ api, bookingId }: { api: string; bookingId: string }) {
  const [open, setOpen] = useState(false);
  const policy = usePolicy(api, bookingId, open);
  return (
    <div className="pt-1">
      <button
        type="button"
        className="flex items-center gap-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        {open ? "Hide cancellation policy" : "View cancellation policy"}
      </button>
      {open && (
        <div className="mt-3 space-y-4 rounded-lg border bg-muted/30 p-4 text-sm">
          {policy.isPending && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading policy…
            </p>
          )}
          {policy.isError && (
            <div className="space-y-2">
              <p className="text-destructive">{messageOf(policy.error, "Failed to load the cancellation policy.")}</p>
              <Button variant="outline" size="sm" onClick={() => void policy.refetch()}>
                Try again
              </Button>
            </div>
          )}
          {policy.data && (
            <>
              <section className="space-y-1.5">
                <h3 className="font-semibold">Cancellation policy</h3>
                {!policy.data.canCancel ? (
                  <p>This booking cannot be cancelled.</p>
                ) : policy.data.cancellationTiers.length === 0 ? (
                  <p>Cancellations are allowed. Please contact us for details.</p>
                ) : (
                  <ul className="list-disc space-y-1 pl-5">
                    {describeCancellationTiers(policy.data.cancellationTiers).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
              </section>
              <section className="space-y-1.5">
                <h3 className="font-semibold">Reschedule policy</h3>
                <p>{describeReschedulePolicy(policy.data)}</p>
              </section>
              {policy.data.additionalTerms && (
                <section className="space-y-1.5">
                  <h3 className="font-semibold">Additional terms</h3>
                  <NoteMarkdown>{policy.data.additionalTerms}</NoteMarkdown>
                </section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The booking id as a QR code, for a quick check-in at the door. */
export function QrCodePanel({ bookingId }: { bookingId: string }) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || src) return;
    let live = true;
    BookingQRCode__DataUrl(bookingId, { width: 140, margin: 2 }).then(
      (url: string) => live && setSrc(url),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [open, src, bookingId]);

  return (
    <div className="px-5 py-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-sm font-medium transition-colors hover:text-foreground/80"
      >
        <span className="flex items-center gap-2">
          <QrCode className="h-4 w-4 text-muted-foreground" />
          Check-in QR code
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 flex flex-col items-center">
          {src ? (
            // eslint-disable-next-line @next/next/no-img-element -- a web component, not a Next page
            <img src={src} alt="Booking QR code" width={140} height={140} className="rounded-lg" />
          ) : failed ? (
            <p className="text-xs text-muted-foreground">The QR code couldn’t be shown. Your booking reference is #{bookingId}.</p>
          ) : (
            <div className="h-[140px] w-[140px] animate-pulse rounded-lg bg-muted" aria-label="Loading" />
          )}
          <p className="mt-3 max-w-[220px] text-center text-xs text-muted-foreground">Show this QR code when you arrive for quick check-in</p>
        </div>
      )}
    </div>
  );
}
